import { MediaQueryByQuery, type Media_Query_By_Query } from '@/graphql/queries/Media.query';
import { Clear } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { Suspense } from 'react';
import { useLazyLoadQuery } from 'react-relay';

type AssetNode = {
  id?: string | null;
  mediaItemUrl?: string | null;
  title?: string;
};

type ImagePreviewProps = {
  node?: AssetNode;
  onClear?: () => void;
  style?: React.CSSProperties;
  slotProps?: {
    img?: React.CSSProperties;
  };
};

const ImagePreview = ({ node, style, onClear, slotProps }: ImagePreviewProps) => {
  if (!node?.mediaItemUrl) return null;

  return (
    <Tooltip title={node?.title}>
      <div
        style={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '20px',
          ...style,
        }}
      >
        {!!onClear ? (
          <Tooltip title="Clear Image">
            <IconButton
              size="small"
              onClick={onClear}
              sx={{
                position: 'absolute',
                top: '0',
                right: '0',
                zIndex: 1,
              }}
            >
              <Clear />
            </IconButton>
          </Tooltip>
        ) : null}
        <img
          src={node?.mediaItemUrl}
          style={{
            background: 'radial-gradient(circle, rgba(204,204,204,1) 0%, rgba(244,244,244,1) 60%)',
            maxWidth: '230px',
            maxHeight: '200px',
            width: 'auto',
            height: 'auto',
            ...slotProps?.img,
          }}
        />
      </div>
    </Tooltip>
  );
};

const Loader = (props: ImagePreviewProps) => {
  const data = useLazyLoadQuery<Media_Query_By_Query>(
    MediaQueryByQuery,
    { id: props?.node?.id ?? '', idType: 'ID' },
    { fetchPolicy: 'store-or-network' }
  );

  return (
    <ImagePreview
      {...props}
      node={{
        ...props.node,
        mediaItemUrl: data?.mediaItem?.mediaItemUrl ?? 'not-found',
      }}
    />
  );
};

const Wrapper = (props: ImagePreviewProps) => {
  if (!!props?.node?.id) {
    return (
      <Suspense fallback={<ImagePreview {...props} />}>
        <Loader {...props} />
      </Suspense>
    );
  }

  return <ImagePreview {...props} />;
};

export { Wrapper as ImagePreview };
